import React, { useState, useEffect, useCallback } from 'react';
import { getUsuarios, deleteUsuario, getPerfiles } from '../../../api/usuarios';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, TextField, CircularProgress, TablePagination, Box } from '@mui/material';
import { Edit, Delete, Visibility, Add, Update, Security } from '@mui/icons-material';
import CreateUsuario from './_Create';
import EditUsuario from './_Edit';
import ViewUsuario from './_ViewById';
import { ConfirmDelete } from '../../../components/ConfirmDelete';
import { useAuth } from '../../../context/auth';
import { Perfiles } from './_Perfiles';

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [perfiles, setPerfiles] = useState<any>([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openPermisos, setOpenPermisos] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { permisos } = useAuth();
  const { Prm_Config_Usr_Crear, Prm_Config_Usr_Modificar, Prm_Config_Usr_Eliminar } = permisos;

  const handleOpenDeleteConfirm = useCallback((id: number) => {
    setDeleteId(id);
    setOpenConfirmDelete(true);
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    setOpenConfirmDelete(false);
    setDeleteId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteId !== null) {
      await deleteUsuario(deleteId);
      fetchUsuarios();
      handleCloseDeleteConfirm();
    }
  }, [deleteId, handleCloseDeleteConfirm]);

  useEffect(() => {
    fetchPerfiles();
  }, []);

  useEffect(() => {
    if (perfiles.length > 0) {
      fetchUsuarios();
    }
  }, [perfiles]);

  const fetchUsuarios = useCallback(async () => {
    setLoading(true);
    const response = await getUsuarios();
    if (response.isOk) {
      setUsuarios(response.data);
    }
    setLoading(false);
  }, []);

  const fetchPerfiles = useCallback(async () => {
    setLoading(true);
    const response = await getPerfiles();
    if (response.isOk) {
      setPerfiles(response.data);
    }
    setLoading(false);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const filteredUsuarios = usuarios.filter((usuario: any) => usuario.Us_Nombre.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div style={{ marginTop: '5px' }}>
      {loading ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          <CircularProgress />
        </Box>
      ) : (
        <div style={{ marginTop: '5px' }}>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <TextField label='Buscar' value={searchTerm} onChange={handleSearchChange} fullWidth size='small' />
            {Prm_Config_Usr_Crear === 1 && (
              <IconButton onClick={() => setOpenCreate(true)} title='Nuevo' className='tool-btn'>
                <Add color='success' />
              </IconButton>
            )}
            <IconButton onClick={fetchUsuarios} title='Actualizar' className='tool-btn'>
              <Update />
            </IconButton>
            <IconButton onClick={() => setOpenPermisos(true)} title='Permisos' className='tool-btn'>
              <Security />
            </IconButton>
          </Box>
          <TableContainer component={Paper}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Perfil</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsuarios.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((usuario: any) => (
                  <TableRow key={usuario.Us_Id}>
                    <TableCell>{usuario.Us_Id}</TableCell>
                    <TableCell>{usuario.Us_Nombre}</TableCell>
                    <TableCell>{usuario.Us_Usuario}</TableCell>
                    <TableCell>{perfiles.filter((x: any) => x.Pe_Id === usuario.Us_Perfil_Id)[0].Pe_Nombre}</TableCell>
                    <TableCell>{usuario.Us_Estado ? 'Activo' : 'Inactivo'}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setSelectedUsuario(usuario);
                          setOpenView(true);
                        }}>
                        <Visibility color='info' />
                      </IconButton>
                      {Prm_Config_Usr_Modificar === 1 && (
                        <IconButton
                          onClick={() => {
                            setSelectedUsuario(usuario);
                            setOpenEdit(true);
                          }}>
                          <Edit color='primary' />
                        </IconButton>
                      )}
                      {Prm_Config_Usr_Eliminar === 1 && (
                        <IconButton onClick={() => handleOpenDeleteConfirm(usuario.Us_Id)} title='Eliminar'>
                          <Delete color='error' />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component='div' count={filteredUsuarios.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} />
        </div>
      )}

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)}>
        <DialogTitle>Crear Usuario</DialogTitle>
        <DialogContent>
          <CreateUsuario
            LstPerfiles={perfiles}
            onClose={() => {
              setOpenCreate(false);
              fetchUsuarios();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Editar Usuario</DialogTitle>
        <DialogContent>
          <EditUsuario
            LstPerfiles={perfiles}
            usuario={selectedUsuario}
            onClose={() => {
              setOpenEdit(false);
              fetchUsuarios();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openView} onClose={() => setOpenView(false)}>
        <DialogTitle>Ver Usuario</DialogTitle>
        <DialogContent>
          <ViewUsuario usuario={selectedUsuario} onClose={() => setOpenView(false)} />
        </DialogContent>
      </Dialog>

      <Dialog maxWidth='lg' fullWidth open={openPermisos} onClose={() => setOpenPermisos(false)}>
        <DialogTitle>Permisos</DialogTitle>
        <DialogContent>
          <Perfiles />
        </DialogContent>
      </Dialog>

      <ConfirmDelete open={openConfirmDelete} onClose={handleCloseDeleteConfirm} onConfirm={handleConfirmDelete} message='¿Estás seguro de que deseas eliminar este usuario?' />
    </div>
  );
};

export default Usuarios;
